import React, { useState, useCallback, useMemo } from "react";
import { Box, Typography, Checkbox, Button, Divider } from "@mui/material";
import LibraryView from "./LibraryView";
import LoadingOverlay from "./LoadingOverlay";
import { useGetUsersByCompany } from "../api/reactQueryHook/usePeople";
import { useTheme } from "@mui/material/styles";
import AcknowledgementMatrixView from "./AcknowledgementMatrixView";
import { useGetAcknowledgementMatrix, useSendReminder } from "../api/reactQueryHook/useFormAssignments";
import * as XLSX from "xlsx";
import * as XLSXStyle from "xlsx-js-style";

interface MatrixViewProps {
  selectedCompanyId: number;
}

type Step = "files" | "users" | "matrix";

const MatrixView: React.FC<MatrixViewProps> = ({ selectedCompanyId }) => {
  const [step, setStep]                   = useState<Step>("files");
  const [selected, setSelected]           = useState<Set<string>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [allKeys, setAllKeys]             = useState<Set<string>>(new Set());

  const { data: matrixData, refetch: refetchMatrix, isPending: matrixLoading } = useGetAcknowledgementMatrix(selectedCompanyId);
  const matrixRows = matrixData?.data ?? [];
  const sendReminderMutation = useSendReminder(selectedCompanyId);


  // ── File selection helpers ──
  const allFilesChecked    = allKeys.size > 0 && selected.size === allKeys.size;
  const filesIndeterminate = selected.size > 0 && !allFilesChecked;

  const handleToggle = (keys: string[], checked: boolean) => {
    setSelected(prev => {
      const next = new Set(prev);
      keys.forEach(k => checked ? next.add(k) : next.delete(k));
      return next;
    });
  };

  const registerKeys = useCallback((keys: string[]) => {
    setAllKeys(prev => {
      const next = new Set(prev);
      keys.forEach(k => next.add(k));
      return next;
    });
  }, []);

  // ── User selection helpers ──
  const { data: peopleData, isPending: usersLoading } = useGetUsersByCompany(selectedCompanyId);
  const users = useMemo(() =>
    [...(peopleData?.data.find(c => c.company_id === selectedCompanyId)?.user_info ?? [])].sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
    ),
    [peopleData, selectedCompanyId]
  );

  const allUsersChecked    = selectedUsers.size === users.length && users.length > 0;
  const usersIndeterminate = selectedUsers.size > 0 && !allUsersChecked;

  const handleUserToggle = (userId: number, checked: boolean) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      checked ? next.add(userId) : next.delete(userId);
      return next;
    });
  };

  const handleSelectAllUsers = (checked: boolean) => {
    setSelectedUsers(checked ? new Set(users.map(u => u.user_id)) : new Set());
  };

  // ── Export Matrix to Excel (screen-faithful layout, with cell styling) ──
  const handleExportMatrix = () => {
    const selectedUserList = users.filter(u => selectedUsers.has(u.user_id));

    const tree: Record<string, Record<string, string[]>> = {};
    selected.forEach(key => {
      const [lib, dept, file] = key.split("||");
      if (!tree[lib]) tree[lib] = {};
      if (!tree[lib][dept]) tree[lib][dept] = [];
      tree[lib][dept].push(file);
    });

    const totalCols = 1 + selectedUserList.length;
    const aoa: string[][] = [];
    const merges: XLSX.Range[] = [];
    const libRows:  number[]   = [];
    const deptRows: number[]   = [];

    // Row 0 — header: blank + user names
    aoa.push(["", ...selectedUserList.map(u => `${u.first_name} ${u.last_name}`)]);

    Object.entries(tree).forEach(([lib, depts]) => {
      const libRowIdx = aoa.length;
      libRows.push(libRowIdx);
      aoa.push([lib, ...Array(selectedUserList.length).fill("")]);
      merges.push({ s: { r: libRowIdx, c: 0 }, e: { r: libRowIdx, c: totalCols - 1 } });

      Object.entries(depts).forEach(([dept, files]) => {
        const deptRowIdx = aoa.length;
        deptRows.push(deptRowIdx);
        aoa.push([dept, ...Array(selectedUserList.length).fill("")]);
        merges.push({ s: { r: deptRowIdx, c: 0 }, e: { r: deptRowIdx, c: totalCols - 1 } });

        files.forEach(file => {
          aoa.push([
            file,
            ...selectedUserList.map(u => {
              const row = matrixRows.find(r => r.file_name === file && r.user_id === u.user_id);
              return row?.submitted ? "✓" : row ? "✗" : "—";
            }),
          ]);
        });
      });
    });

    const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;
    ws["!cols"]   = [{ wch: 36 }, ...selectedUserList.map(() => ({ wch: 20 }))];

    const numRows = aoa.length;

    // ── Apply styles row by row ──
    for (let r = 0; r < numRows; r++) {
      for (let c = 0; c < totalCols; c++) {
        const cellRef = XLSX.utils.encode_cell({ r, c });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };

        const isLib  = libRows.includes(r);
        const isDept = deptRows.includes(r);
        const isHeader = r === 0;

        // Design guide palette (hex without #)
        // Primary: 1778C7 | Accent: 46A0EB | Dark bg: 171717 | Paper: 262626
        // Success: 10B981 | Error: EF4444
        const bgColor = isHeader ? "1778C7"  // primary blue — user header row
                      : isLib    ? "171717"  // darkest bg — library row
                      : isDept   ? "E8E8EE"  // light gray — department row
                      : "F5F5F5";            // very light — file row

        const cellVal = ws[cellRef]?.v ?? "";
        const isSubmitted = cellVal === "✓";
        const isPending   = cellVal === "✗";

        const fontColor = (isHeader || isLib)
          ? "FFFFFF"
          : isDept                       ? "262626"   // dark text on light dept row
          : (c === 0)                    ? "262626"   // file name column — dark
          : isSubmitted                  ? "10B981"   // success green
          : isPending                    ? "EF4444"   // error red
          : "AAAAAA";                                 // muted for —

        ws[cellRef].s = {
          fill: bgColor ? { fgColor: { rgb: bgColor } } : undefined,
          font: {
            bold: isLib || isDept || isHeader || isSubmitted || isPending,
            color: { rgb: fontColor },
            sz: 11,
          },
          alignment: {
            vertical: "center",
            horizontal: c === 0 ? "left" : "center",
            wrapText: false,
          },
          border: {
            top:    { style: "thin", color: { rgb: "2C2C2C" } },
            bottom: { style: "thin", color: { rgb: "2C2C2C" } },
            left:   { style: "thin", color: { rgb: "2C2C2C" } },
            right:  { style: "thin", color: { rgb: "2C2C2C" } },
          },
        };
      }
    }

    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, ws, "Acknowledgement Matrix");
    XLSXStyle.writeFile(wb, `acknowledgement_matrix_${selectedCompanyId}.xlsx`);
  };

  const theme = useTheme();

  // ── Step 3 — matrix view ──
  if (step === "matrix") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <AcknowledgementMatrixView
          selectedFiles={selected}
          selectedUserIds={selectedUsers}
          users={users}
          matrixRows={matrixRows}
          onSendReminder={(assignment_id) =>
            sendReminderMutation.mutate(assignment_id, {
              onSuccess: () => refetchMatrix(),
            })
          }
          onEditMatrix={() => setStep("files")}
          onExportMatrix={handleExportMatrix}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <LoadingOverlay isLoading={matrixLoading || usersLoading} />

      {/* Header */}
      <Box sx={{ mx: 2, mt: 3, mb: 1.5, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ px: 2, mb: 1.5, color: "text.primary", fontWeight: 500 }}>
          {step === "files" ? "CHOOSE YOUR FILES FOR THE MATRIX" : "CHOOSE USERS FOR THE MATRIX"}
        </Typography>
        <Divider sx={{ mx: 2, borderColor: "divider", borderBottomWidth: 1.5, my: 1 }} />

        {step === "files" ? (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, px: 2, cursor: "pointer" }}
            onClick={() => handleToggle([...allKeys], !allFilesChecked)}
          >
            <Checkbox
              checked={allFilesChecked}
              indeterminate={filesIndeterminate}
              onChange={e => handleToggle([...allKeys], e.target.checked)}
              sx={{ p: 0, color: "text.secondary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
            />
            <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 500 }}>Select All</Typography>
          </Box>
        ) : (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, px: 2, cursor: "pointer" }}
            onClick={() => handleSelectAllUsers(!allUsersChecked)}
          >
            <Checkbox
              checked={allUsersChecked}
              indeterminate={usersIndeterminate}
              onChange={e => handleSelectAllUsers(e.target.checked)}
              sx={{ p: 0, color: "text.disabled", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
            />
            <Typography variant="body1" sx={{ color: "text.primary", fontWeight: 500 }}>Select All</Typography>
          </Box>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {step === "files" ? (
          <LibraryView
            selectedCompanyId={selectedCompanyId}
            showCheckbox={true}
            selected={selected}
            onToggle={handleToggle}
            allKeys={allKeys}
            onKeysDiscovered={registerKeys}
          />
        ) : (
          <Box sx={{ flex: 1, overflowY: "auto", px: 4, pt: 3, pb: 2, height: "100%" }}>
            {users.length > 0 ? users.map(user => {
              const checked = selectedUsers.has(user.user_id);
              return (
                <Box
                  key={user.user_id}
                  onClick={() => handleUserToggle(user.user_id, !checked)}
                  sx={{
                    display: "flex", alignItems: "center", gap: 2,
                    px: 3, py: 2, mb: 1.5, borderRadius: "10px", minHeight: "72px",
                    backgroundColor: checked ? "action.selected" : "background.paper",
                    border: `1px solid ${checked ? theme.palette.primary.main : theme.palette.divider}`,
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "action.hover" },
                    transition: "background-color 0.15s ease, border-color 0.15s ease",
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onChange={e => { e.stopPropagation(); handleUserToggle(user.user_id, e.target.checked); }}
                    size="small"
                    sx={{ p: 0, color: "text.secondary", "&.Mui-checked": { color: "primary.main" } }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ color: "text.primary", fontSize: "20px", fontWeight: 500, fontFamily: "Roboto, Helvetica, Arial, sans-serif" }}>
                      {user.first_name} {user.last_name} ({user.email_id})
                    </Typography>
                  </Box>
                </Box>
              );
            }) : (
              <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", mt: 6 }}>
                No users found.
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 2, borderTop: "1px solid", borderColor: "divider" }}>
        {step === "users" && (
          <Button
            variant="contained"
            onClick={() => setStep("files")}
            sx={{ fontWeight: 500, py: 1.8, px: 5, borderRadius: "4px", fontSize: "0.85rem", background: `${theme.palette.error.main} !important`, "&:hover": { background: `${theme.palette.error.dark} !important` }, minWidth: 200 }}
          >
            GO BACK
          </Button>
        )}
        <Button
          variant="contained"
          disabled={step === "files" ? selected.size === 0 : selectedUsers.size === 0}
          onClick={() => { if (step === "files") setStep("users"); else if (step === "users") setStep("matrix"); }}
          sx={{ fontWeight: 500, px: 4, py: 1.2, borderRadius: "4px", letterSpacing: "0.1rem" }}
        >
          SAVE & PROCEED
        </Button>
      </Box>

    </Box>
  );
};

export default MatrixView;